import { toast } from 'sonner';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { useCurrentTenantQuery } from '@/modules/companies/queries/useCurrentTenantQuery';
import { useUpdateSignupFlowMutation } from '@/modules/signup-flow/mutations/useUpdateSignupFlowMutation';
import { signupFlowStep } from '@/modules/signup-flow/signupFlowSteps';

import { type HiringGoals, HiringGoalsForm } from './HiringGoalsForm';

const DEFAULT_GOALS: HiringGoals = {
  companyStage: 'Seed',
  numberOfHires: '1-3',
};

/** Step 2: hiring goals. Back and Continue both persist the step. */
export const CompanyGoals = () => {
  const trackEvent = useTrackEvent();
  const currentTenantQuery = useCurrentTenantQuery();
  const updateSignupFlowMutation = useUpdateSignupFlowMutation();

  const persisted = currentTenantQuery.data?.onboarding?.state;

  const onBack = () => {
    updateSignupFlowMutation.mutate(
      { step: signupFlowStep.aboutCompany },
      {
        onError: () => {
          toast.error('Failed to go back. Please try again.');
        },
      },
    );
  };

  const onContinue = (goals: HiringGoals) => {
    updateSignupFlowMutation.mutate(
      { step: signupFlowStep.slack, state: goals },
      {
        onError: () => {
          toast.error(
            'Failed to set up your hiring goals. Please try again or contact support.',
          );
        },
        onSuccess: () => {
          trackEvent('companyGoalsFinished', goals);
        },
      },
    );
  };

  return (
    <HiringGoalsForm
      initial={{
        companyStage: persisted?.companyStage || DEFAULT_GOALS.companyStage,
        numberOfHires: persisted?.numberOfHires || DEFAULT_GOALS.numberOfHires,
      }}
      isSubmitting={updateSignupFlowMutation.isPending}
      onBack={onBack}
      onContinue={onContinue}
    />
  );
};
